import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { useLocation, useParams } from 'react-router-dom';
import MessageComposer from '../components/feed/MessageComposer';
import MessageList from '../components/feed/MessageList';
import TrendingHashtags from '../components/common/TrendingHashtags';
import AuthModal from '../components/auth/AuthModal';
import { useUniversity } from '../contexts/UniversityContext';
import UniversitySelectModal from '../components/university/UniversitySelectModal';
import { FaUniversity } from 'react-icons/fa';
import { FaExchangeAlt } from 'react-icons/fa';

// Definir animación de aparición suave
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 15px;
  padding-top: 15px;
  display: grid;
  grid-template-columns: 1fr 3fr 1fr;
  gap: 15px;
  margin-left: 70px; /* Espacio para el navbar lateral */
  padding-left: 15px;
  min-height: calc(100vh - 50px);
  box-sizing: border-box;
  animation: ${fadeIn} 0.3s ease-out;
  position: relative;
  
  @media (max-width: 1200px) {
    max-width: 100%;
    margin-left: 70px;
    padding-left: 15px;
  }
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr 2fr;
    margin-left: 70px;
    padding-left: 15px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 10px;
    padding-top: 10px;
    gap: 10px;
    margin-bottom: 56px;
    margin-left: 0;
    padding-left: 10px;
  }

  @media (max-height: 600px) {
    min-height: calc(100vh - 50px);
    padding: 10px;
    padding-top: 10px;
  }
`;

const LeftSidebar = styled.div`
  position: sticky;
  top: 65px; /* Aumentado para mejor posicionamiento */
  height: fit-content;
  max-height: calc(100vh - 80px);
  overflow: visible;
  padding-right: 15px;
  align-self: flex-start;
  will-change: transform; /* Ayuda a crear un nuevo contexto de apilamiento */
  transform: translateZ(0); /* Fuerza la aceleración por hardware */
  z-index: 10; /* Asegura que esté por encima del contenido principal al desplazarse */
  margin-top: 0;
  
  /* Estilizar scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollThumb};
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const RightSidebar = styled.div`
  position: sticky;
  top: 50px; /* Reducido de 65px para alinear con el header */
  height: calc(100vh - 50px); /* Ajustado para considerar solo el navbar */
  overflow-y: auto;

  /* Estilizar scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollThumb};
    border-radius: 3px;
  }
  
  @media (max-width: 992px) {
    display: none;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  position: relative;
  transform: translateZ(0);
  width: 100%;
  min-height: calc(100vh - 50px); /* Ajustado para considerar solo el navbar */
  margin-top: 0;
  
  @media (max-width: 768px) {
    gap: 8px;
    min-height: auto;
  }

  @media (max-height: 600px) {
    min-height: auto;
    gap: 6px;
  }
`;

const WelcomeCard = styled.div`
  background: ${props => props.theme.card};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  padding: 20px; /* Reducido de 30px */
  text-align: center;
  margin-bottom: 15px; /* Reducido de 20px */
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 15px;
    margin-bottom: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    margin-bottom: 8px;
  }

  @media (max-height: 600px) {
    padding: 10px;
    margin-bottom: 6px;
  }
`;

const WelcomeTitle = styled.h2`
  color: ${props => props.theme.primary};
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 16px;
  position: relative;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.primary};
    border-radius: 2px;
    
    @media (max-width: 480px) {
      width: 40px;
    }
  }
`;

const WelcomeText = styled.p`
  margin-bottom: 24px;
  color: ${props => props.theme.text};
  font-size: 16px;
  line-height: 1.6;
  max-width: 90%;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    max-width: 100%;
    margin-bottom: 16px;
  }
  
  strong {
    color: ${props => props.theme.primary};
    font-weight: 600;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  background-color: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 14px;
  }
  
  &:hover {
    background-color: ${props => props.theme.primaryHover};
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const MobileTrendingSection = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    margin: 8px 0; /* Reducido de 15px */
    width: 100%;
    padding: 0 5px;
    box-sizing: border-box;
    
    & > div {
      padding: 10px;
      width: 100%;
      box-sizing: border-box;
      margin-bottom: 10px;
      overflow: hidden;
      transition: all 0.3s ease;
    }
    
    & h3 {
      margin-bottom: 8px;
      font-size: 15px;
    }
    
    & ul {
      min-height: 180px;
      width: 100%;
    }
    
    & li {
      padding: 6px 0;
    }
  }
  
  @media (max-width: 480px) {
    margin: 6px 0; /* Reducido de 10px */
    padding: 0 3px;
    
    & > div {
      padding: 8px;
    }
    
    & h3 {
      margin-bottom: 6px;
      font-size: 14px;
    }
    
    & ul {
      min-height: 150px;
    }
    
    & li {
      padding: 4px 0;
    }
  }
`;

const ReturnButton = styled(Button)`
  background-color: ${props => props.theme.primary};
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 14px;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 15px;
  
  &:hover {
    background-color: ${props => props.theme.primaryHover};
  }
  
  &:disabled {
    background-color: ${props => props.theme.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

// Contenedor con posición relativa para mantener un layout estable
const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  transition: all 0.3s ease-out;
  will-change: transform, opacity;
  transform-style: preserve-3d;
  backface-visibility: hidden;
  contain: layout style paint;
  opacity: ${props => props.$ready ? 1 : 0};
  margin-top: 0; /* Eliminamos margen superior extra */
  
  &:before {
    content: '';
    display: block;
    position: relative;
    width: 100%;
    margin-top: 0; /* Eliminamos margen superior extra */
  }
  
  @media (max-width: 768px) {
    min-height: auto;
  }
  
  @media (max-width: 480px) {
    min-height: auto;
  }

  @media (max-height: 600px) {
    min-height: auto;
  }
`;

// Agregar un HeaderContainer para el encabezado de la página
const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    margin-bottom: 10px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 8px;
  }
`;

// Componente para mostrar la universidad actual
const CurrentUniversityDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 20px;
  background-color: ${props => props.theme.cardBg};
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.backgroundAlt};
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 5px 10px;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 4px 8px;
    gap: 5px;
  }
`;

// Icono de la universidad
const UniversityIcon = styled.div`
  width: 30px;
  height: 30px;
  background-color: ${props => props.$color || '#2E7D32'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 15px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    background-color: white;
  }
  
  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }
`;

// Texto de la universidad
const UniversityName = styled.span`
  font-weight: 500;
  color: ${props => props.theme.text};
  font-size: 14px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  
  @media (max-width: 768px) {
    font-size: 13px;
    max-width: 150px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    max-width: 120px;
  }
`;

const ChangeText = styled.span`
  font-size: 12px;
  color: ${props => props.theme.primary};
  display: flex;
  align-items: center;
  gap: 4px;
  
  svg {
    font-size: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
    
    svg {
      font-size: 8px;
    }
  }
`;

const Home = () => {
  const { isAuthenticated, user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUniversityModal, setShowUniversityModal] = useState(false);
  const [selectedHashtag, setSelectedHashtag] = useState(null);
  const { currentUniversity, changeUniversity, isChanging } = useUniversity();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const location = useLocation();
  const [trendingComponent, setTrendingComponent] = useState(null);
  const { hashtag } = useParams();
  const [contentReady, setContentReady] = useState(false);
  
  // Ref para controlar el estado de montaje y evitar efectos no deseados en desmontaje
  const isMountedRef = useRef(true);
  
  // Limpieza cuando el componente se desmonta
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // Efecto para controlar la visibilidad del contenido
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setContentReady(true);
      }
    }, 150);
    
    return () => {
      clearTimeout(timer);
      setContentReady(false);
    };
  }, [location.pathname]);
  
  // Scroll a la parte superior cuando se carga la página
  useEffect(() => {
    // Verificar si debemos hacer scroll basado en el estado de navegación
    const shouldScroll = location.state?.shouldScrollTop || false;
    if (shouldScroll) {
      // Garantizar que el scroll ocurre después de montar el componente
      window.scrollTo({
        top: 0,
        behavior: 'auto'
      });
    }
  }, [location.state]);
  
  // Scroll inicial solo en carga de la aplicación
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, []);
  
  // Check if university has been selected - with double-check verification
  useEffect(() => {
    const storedHasSelected = localStorage.getItem('hasSelectedUniversity');
    const storedUniversity = localStorage.getItem('selectedUniversity');
    
    // Show modal if:
    // 1. User has never made a selection (no flag in localStorage), OR
    // 2. User has no valid university saved (ensures persistence across refreshes)
    if (!storedHasSelected || !storedUniversity) {
      setShowUniversityModal(true);
    }
  }, []);
  
  // Handle university modal closing - only allow if a selection has been made
  const handleCloseUniversityModal = () => {
    setShowUniversityModal(false);
  };
  
  // Show auth modal when user tries to interact without being authenticated
  const handleAuthPrompt = useCallback(() => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
    }
  }, [isAuthenticated]);
  
  // Handle university change with debounce protection
  const handleReturnToMyUniversity = () => {
    if (isButtonDisabled || isChanging || !user || !user.university) return;
    
    setIsButtonDisabled(true);
    changeUniversity(user.university);
    localStorage.setItem('selectedUniversity', user.university);
    
    // Remove scroll to top when changing university
    
    setTimeout(() => {
      setIsButtonDisabled(false);
    }, 2000);
  };
  
  // Handle hashtag changes from URL or params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hashtagParam = searchParams.get('hashtag') || hashtag;
    
    if (hashtagParam) {
      setSelectedHashtag(hashtagParam);
    } else {
      setSelectedHashtag(null);
    }
  }, [location.search, hashtag]);
  
  const handleHashtagClick = useCallback((hashtag) => {
    setSelectedHashtag(hashtag);
    // Actualizar URL sin necesidad de redirección completa
    const url = new URL(window.location);
    url.searchParams.set('hashtag', hashtag);
    window.history.pushState({}, '', url);
  }, []);
  
  const clearHashtagFilter = useCallback(() => {
    setSelectedHashtag(null);
    // Limpiar parámetro de URL
    const url = new URL(window.location);
    url.searchParams.delete('hashtag');
    window.history.pushState({}, '', url);
  }, []);
  
  // Función para abrir el modal de autenticación
  const handleOpenAuthModal = () => {
    setShowAuthModal(true);
  };
  
  // Crear una única instancia de TrendingHashtags que se compartirá
  useEffect(() => {
    if (isMountedRef.current) {
      setTrendingComponent(<TrendingHashtags onHashtagClick={handleHashtagClick} />);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Crear componentes de forma estable usando useMemo para evitar recreaciones
  const messageComposer = useMemo(() => (
    <MessageComposer 
      onAuthNeeded={handleAuthPrompt} 
      key={`composer-${currentUniversity?.id || 'none'}`}
    />
  ), [currentUniversity?.id, handleAuthPrompt]);
  
  const messageList = useMemo(() => (
    <MessageList 
      hashtag={selectedHashtag}
      onHashtagClick={handleHashtagClick}
      onClearHashtag={clearHashtagFilter}
      key={`list-${selectedHashtag || 'all'}-${currentUniversity?.id || 'none'}`}
    />
  ), [selectedHashtag, currentUniversity?.id, handleHashtagClick, clearHashtagFilter]);
  
  return (
    <Container>
      <LeftSidebar>
        {trendingComponent}
      </LeftSidebar>
      
      <MainContent>
        {/* Header con selector de universidad */}
        <HeaderContainer>
          {currentUniversity ? (
            <CurrentUniversityDisplay onClick={() => setShowUniversityModal(true)}>
              <UniversityIcon $color={currentUniversity.color}>
                {currentUniversity.logo ? (
                  <img 
                    src={currentUniversity.logo}
                    alt={currentUniversity.name}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      borderRadius: '50%',
                      backgroundColor: 'white'
                    }}
                    crossOrigin="anonymous"
                  />
                ) : (
                  <FaUniversity />
                )}
              </UniversityIcon>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <UniversityName>{currentUniversity.name}</UniversityName>
                <ChangeText>
                  <FaExchangeAlt /> Cambiar universidad
                </ChangeText>
              </div>
            </CurrentUniversityDisplay>
          ) : (
            <Button onClick={() => setShowUniversityModal(true)}>
              Seleccionar Universidad
            </Button>
          )}
          
          {isAuthenticated && user && currentUniversity && 
            user.university !== currentUniversity.id && (
            <ReturnButton 
              onClick={handleReturnToMyUniversity} 
              disabled={isButtonDisabled || isChanging}
            >
              {isButtonDisabled ? 'Cambiando...' : 'Volver a mi universidad'}
            </ReturnButton>
          )}
        </HeaderContainer>
        
        {!isAuthenticated && currentUniversity && (
          <WelcomeCard>
            <WelcomeTitle>¡Bienvenido a Sapotix! 🎭</WelcomeTitle>
            <WelcomeText>
              Inicia sesión o regístrate de forma anónima. <strong>Debemos verificar que perteneces a {currentUniversity.name}</strong> para brindarte acceso completo a nuestra comunidad anónima.
            </WelcomeText>
            <Button onClick={() => setShowAuthModal(true)}>
              Iniciar sesión / Registrarse
            </Button>
          </WelcomeCard>
        )}
        
        {/* Envolver componentes en un contenedor estable - MessageComposer primero */}
        <ContentWrapper $ready={contentReady}>
          {messageComposer}
          
          {/* Tendencias móviles (solo visible en pantallas pequeñas) - Ahora después del composer */}
          <MobileTrendingSection>
            {trendingComponent}
          </MobileTrendingSection>
          
          {messageList}
        </ContentWrapper>
      </MainContent>
      
      <RightSidebar>
        {/* Aquí puedes agregar componentes adicionales como sugerencias, etc. */}
      </RightSidebar>
      
      {/* Modals */}
      <AuthModal 
        show={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        initialTab="login"
      />
      
      <UniversitySelectModal
        show={showUniversityModal}
        onClose={handleCloseUniversityModal}
        isRequired={!currentUniversity}
        onOpenAuthModal={handleOpenAuthModal}
      />
    </Container>
  );
};

export default Home;